import React, {useState} from "react";
import {IRelated} from "../Cards/types";
import {Box, Typography} from "@mui/material";
import styles from "./ListTV.module.css";
import clsx from "clsx";
import {useDispatch, useSelector} from "react-redux";
import {setActiveTV, setSelectedTV} from "../../store/activeTV/activeTVSlice";
import {AppDispatch, RootState} from "../../store";
import {closeVideoPlayer, openVideoPlayer, showEventNotStartedOverlay} from "../../store/player/playerSlice";
import helper from "../../constants/helper";
import CustomChip from "../Chip/Chip";
import {useTranslation} from "react-i18next";
import useResize from "../../hooks/useResize";
import {getProgramState, handlePlayCustom} from "../../utils";
import {ProgramPages} from "../../types/page";
import {useLazyGetContentDetailsQuery} from "../../api/modules";

interface IProps {
    data: IRelated[];
    forPlayer?: boolean;
    footer?: React.ReactNode;
    scrollContainerRef?: React.Ref<HTMLDivElement>;
}

const ListTv: React.FC<IProps> = ({data, forPlayer, footer, scrollContainerRef}) => {
    const {innerWidth} = useResize()
    const isMobile = innerWidth < 860
    const {t} = useTranslation("translations")
    const {activeTV} = useSelector((state: RootState) => state.tvContent);
    const {contentContext, eventNotStartedOverlay} = useSelector((state: RootState) => state.videoPlayer);
    const dispatch: AppDispatch = useDispatch();
    const profileID = localStorage.getItem(helper.EPIC_PROFILE_ID);
    const [getContentDetails] = useLazyGetContentDetailsQuery();
    const [_isLoader, setIsLoader] = useState(false);
    const [pendingProgramSelectionId, setPendingProgramSelectionId] = useState<number | null>(null);

    const handleSelect = async (content: IRelated) => {
        const contentId = Number(content.id);

        dispatch(setActiveTV(content));
        isMobile && dispatch(setSelectedTV(true))
        if (forPlayer && profileID && contentId) {
            setPendingProgramSelectionId(contentId);
            try {
                const isSportsContext = contentContext?.page === ProgramPages.SPORTS;
                const isNotStartedFromList = Boolean(
                    content.start &&
                    content.end &&
                    !getProgramState(content.start, content.end).isStarted
                );

                if (isSportsContext && isNotStartedFromList) {
                    dispatch(showEventNotStartedOverlay({
                        poster: content.backdrop_path,
                        title: content.title,
                    }));
                    return;
                }

                const programType = contentContext?.page ?? content.type ?? ProgramPages.LIVE;

                dispatch(openVideoPlayer({
                    id: contentId,
                    title: content.title || "",
                    name: content.title || "",
                    poster_path: content.poster_path || content.backdrop_path || "",
                    type: programType,
                    videoData: content.video ?? {id: null, url: "", subtitle: ""},
                    contentContext,
                }));

                if (!content.video?.url) {
                    await handlePlayCustom({
                        profileId: profileID,
                        data: contentId,
                        dispatch,
                        setIsLoader,
                        getContentDetails,
                        openVideoPlayer,
                        contentContext,
                        onError: () => dispatch(closeVideoPlayer()),
                    });
                }
            } catch (err) {
                console.error(err)
            } finally {
                setPendingProgramSelectionId(null);
            }
        }
    };


    return (
        <Box className={clsx(styles.wrapper, forPlayer && styles.wrapperPlayer)} ref={scrollContainerRef}>
            {data && data.map((content: IRelated, index: React.Key | null | undefined) => (
                <div
                    className={clsx(styles.card, activeTV?.id === content.id && styles.active, forPlayer && styles.cardBlur,
                        (activeTV?.id === content.id && forPlayer) && styles.activeBlur
                    )} key={content.id ?? index}
                    onClick={() => handleSelect(content)}>
                    <img src={content.backdrop_path} alt={"poster"} loading={"lazy"}/>
                    <div>
                        {activeTV?.id === content.id &&
                          !eventNotStartedOverlay?.visible &&
                          pendingProgramSelectionId !== Number(content.id)
                          && <div className={clsx(styles.chipLiveProgram)}>
                          <CustomChip label={t("Details.live")} color="error"/>
                        </div>}
                        <Typography variant={"h2"}>{content.title}</Typography>
                        <Typography variant={"h4"}>{content.synopsis}</Typography>
                    </div>
                </div>
            ))}
            {footer}
        </Box>
    );
};

export default ListTv;
